import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';

export default function OrderReceiverDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, confirmed: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError('');
      try {
        const [listRes, countsRes] = await Promise.all([
          apiService.orders.getSalesOrders(),
          apiService.orders.getSalesOrderCounts(),
        ]);
        if (!cancelled && Array.isArray(listRes.data)) setOrders(listRes.data.slice(0, 10));
        if (!cancelled && countsRes.data) setCounts(countsRes.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const displayName = user?.name?.split(' ')[0] || 'User';

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-1">Hello, {displayName}!</h1>
        <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">Orders sent by sales — confirm or reject. Showing today&apos;s orders only.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <button
            type="button"
            onClick={() => navigate('/order-receiver/orders')}
            className="min-h-[44px] sm:min-h-0 bg-orange-200 hover:bg-orange-300 text-gray-800 rounded-xl py-4 px-3 flex flex-col items-center justify-center border border-orange-300 touch-manipulation"
          >
            <span className="text-2xl mb-2">🕐</span>
            <span className="text-sm font-semibold">Pending</span>
            {!loading && <span className="text-lg font-bold mt-1">{counts.pending}</span>}
          </button>
          <div className="min-h-[44px] sm:min-h-0 bg-green-200 rounded-xl py-4 px-3 flex flex-col items-center justify-center border border-green-300">
            <span className="text-2xl mb-2">✓</span>
            <span className="text-sm font-semibold">Confirmed</span>
            {!loading && <span className="text-lg font-bold mt-1">{counts.confirmed}</span>}
          </div>
          <div className="min-h-[44px] sm:min-h-0 bg-red-200 rounded-xl py-4 px-3 flex flex-col items-center justify-center border border-red-300">
            <span className="text-2xl mb-2">✕</span>
            <span className="text-sm font-semibold">Rejected</span>
            {!loading && <span className="text-lg font-bold mt-1">{counts.rejected}</span>}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate('/order-receiver/orders')}
            className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium min-h-[44px] touch-manipulation"
          >
            View all orders
          </button>
          <button
            type="button"
            onClick={() => navigate('/order-receiver/revenue')}
            className="bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium min-h-[44px] touch-manipulation"
          >
            Revenue
          </button>
        </div>

        <h2 className="text-lg font-semibold text-gray-700 mb-3">Recent orders</h2>
        {loading ? (
          <p className="text-gray-500 py-4">Loading…</p>
        ) : orders.length === 0 ? (
          <p className="text-gray-500 py-4">No orders yet.</p>
        ) : (
          <ul className="space-y-2">
            {orders.map((o) => (
              <li
                key={o._id}
                className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg border border-gray-100"
              >
                <span className="font-medium text-gray-800">{o.orderNo}</span>
                <span
                  className={`text-sm px-2 py-0.5 rounded ${
                    o.status === 'Received'
                      ? 'bg-green-100 text-green-800'
                      : o.status === 'Rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-orange-100 text-orange-800'
                  }`}
                >
                  {o.status === 'Sent to Central Kitchen' ? 'Pending' : o.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
